/**
 * Turning a route into something a person can read.
 *
 * The people this dashboard is for are not engineers, and
 * `POST /api/rw/verify/files/:id/ddr-validate` tells them nothing. The real fix
 * is a human name in the action dictionary; this is what shows until someone
 * writes one, and what makes the naming queue reviewable — you can only decide
 * whether a guess is good enough if you can see the guess.
 *
 * It is a guess. It gets the shape right (a verb and a subject) and the domain
 * wrong ("Create agronomist advice" is really "Ask the agronomist"), which is
 * exactly the division of labour intended: mechanical where mechanics suffice,
 * human where they do not.
 */

/**
 * Words that name an operation rather than a thing.
 *
 * Checked against the LAST word of the segment, so `ddr-validate`,
 * `structure-check` and `auto-fix` all resolve — seeds hyphenate freely.
 */
const VERBS = new Set([
  'add', 'advance', 'approve', 'archive', 'assign', 'attach', 'autofill', 'cancel',
  'check', 'clone', 'close', 'confirm', 'copy', 'create', 'delete', 'deny',
  'disable', 'download', 'duplicate', 'edit', 'enable', 'execute', 'export',
  'fix', 'generate', 'import', 'invite', 'join', 'launch', 'leave', 'link',
  'lock', 'login', 'logout', 'merge', 'move', 'open', 'pause', 'poll', 'preview',
  'publish', 'push', 'read', 'recall', 'refresh', 'register', 'reject', 'remove',
  'rename', 'reopen', 'reset', 'resolve', 'restore', 'resume', 'retry', 'revoke',
  'run', 'save', 'scope', 'search', 'send', 'share', 'skip', 'split', 'start',
  'stop', 'submit', 'sync', 'unlock', 'unlink', 'update', 'upload', 'validate',
  'verify', 'view',
])

const ID = ':id'

/** `ddr-validate` → `ddr validate`; `supply_base` → `supply base`. */
function words(segment: string): string {
  return segment.replace(/[-_]+/g, ' ').trim()
}

function sentenceCase(text: string): string {
  const trimmed = text.trim()
  return trimmed ? trimmed[0].toUpperCase() + trimmed.slice(1) : trimmed
}

function isVerb(segment: string): boolean {
  const parts = words(segment).split(' ')
  return VERBS.has(parts[parts.length - 1].toLowerCase())
}

/** The verb a method implies when the route does not name one. */
function verbForMethod(method: string, hasId: boolean): string {
  switch (method.toUpperCase()) {
    case 'POST': return 'Create'
    case 'PUT':
    case 'PATCH': return 'Update'
    case 'DELETE': return 'Delete'
    // A GET that names one record is someone opening it; a GET on a collection
    // is someone looking at the list.
    default: return hasId ? 'Open' : 'List'
  }
}

export interface DerivedAction {
  label: string
  /** True when the route itself named the operation, so the guess is a good one. */
  explicit: boolean
}

/**
 * Derive a label for one templated route.
 *
 * Subject is capped at the last two resource segments: the full path reads as a
 * breadcrumb ("Rw verify files recall corrections") where two segments read as
 * a thing ("verify files").
 */
export function deriveAction(method: string, template: string): DerivedAction {
  const segments = template.split('/').filter(Boolean)

  // A semantic event reported by a seed's own frontend. Its author already
  // named it, so the name IS the label — "Lot finalized", not the "Open
  // lot.finalized" that the page branch below would produce.
  if (method === 'TRACK') {
    const name = segments.join('/').replace(/\./g, ' ')
    return { label: name ? sentenceCase(words(name)) : 'Tracked event', explicit: true }
  }

  // Not an API route — a page. The only thing that ever happens to a page.
  if (segments[0] !== 'api') {
    const name = segments.filter(s => s !== ID).map(words).join(' ')
    return { label: name ? `Open ${name}` : 'Open home', explicit: false }
  }

  const rest = segments.slice(1)
  const hasId = rest.includes(ID)
  const tail = rest[rest.length - 1]

  // The route names the operation: `.../approve`, `.../ddr-validate`.
  if (tail && tail !== ID && isVerb(tail)) {
    const subject = rest.slice(0, -1).filter(s => s !== ID).slice(-2).map(words).join(' ')
    return {
      label: subject ? `${sentenceCase(words(tail))} — ${subject}` : sentenceCase(words(tail)),
      explicit: true,
    }
  }

  const subject = rest.filter(s => s !== ID).slice(-2).map(words).join(' ')
  const verb = verbForMethod(method, hasId)
  return { label: subject ? `${verb} ${subject}` : verb, explicit: false }
}

/**
 * What to show for one row of the activity list: the human name if someone
 * wrote one, otherwise the derived guess.
 */
export function actionLabel(
  method: string,
  template: string,
  named: string | null | undefined,
): string {
  if (named && named.trim()) return named.trim()
  return deriveAction(method, template).label
}
